import {
  ApiResponse,
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  ChangePasswordRequest,
  User,
  UserNote,
  ContactForm,
  ApiKeyListResponse,
  ApiKeyListParams,
  ContactSubmissionRequest,
  ContactSubmission,
  ContactSubmissionListResponse,
  ContactSubmissionListParams,
  UpdateContactSubmissionRequest,
  ContactStatistics,
  CreateCardRequest,
  CreateCardResponse,
  BoundApiKey,
  BindApiKeyRequest,
  UnbindApiKeyRequest,
  FAQ,
  FAQPublicResponse,
  FAQCreateRequest,
  FAQUpdateRequest,
  FAQListResponse,
  FAQListParams,
  FAQSortRequest,
  AIApp,
  CreateAppRequest,
  UpdateAppRequest,
  AppListRequest,
  AppListResponse,
  AppDetailResponse,
  DownloadResponse,
  BatchUpdateStatusRequest,
  BatchUpdateStatusResponse,
  DeleteResponse,
  AppStats
} from '@/types';

// API配置
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const API_PREFIX = '/api/v1';

// API错误类
export class ApiError extends Error {
  constructor(public code: number, message: string, public data?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

// HTTP客户端类
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // 默认配置
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // 添加认证头
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    console.log('API request:', {
      url,
      method: config.method || 'GET',
      headers: config.headers,
      body: config.body
    });

    try {
      const response = await fetch(url, config);
      console.log('API response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      const data: ApiResponse<T> = await response.json();
      console.log('API response data:', data);

      // 检查业务逻辑错误
      if (data.code !== 200) {
        throw new ApiError(data.code, data.message || '请求失败', data.data);
      }

      return data.data as T;
    } catch (error) {
      console.error('API request error:', error);
      
      if (error instanceof ApiError) {
        throw error;
      }

      // 网络错误或JSON解析错误
      if (error instanceof TypeError) {
        throw new ApiError(0, '网络连接失败，请检查网络设置');
      }

      throw new ApiError(500, '未知错误，请稍后重试');
    }
  }

  // GET请求
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
    });
  }

  // POST请求
  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT请求
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE请求
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // PATCH请求
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// API客户端实例
const apiClient = new ApiClient(API_BASE_URL);

// 认证相关API
export const authApi = {
  // 用户注册
  register: (data: RegisterRequest): Promise<AuthResponse> =>
    apiClient.post(`${API_PREFIX}/auth/register`, data),

  // 用户登录
  login: (data: LoginRequest): Promise<AuthResponse> =>
    apiClient.post(`${API_PREFIX}/auth/login`, data),

  // 获取当前用户信息
  me: (): Promise<User> =>
    apiClient.get(`${API_PREFIX}/auth/me`),

  // 获取邀请用户列表
  getInvitedUsers: (): Promise<{ total_count: number; invited_users: User[] }> =>
    apiClient.get(`${API_PREFIX}/auth/invited-users`),
  getInvitedUsersPaginated: (page: number = 1, limit: number = 10): Promise<{
    total_count: number;
    invited_users: User[];
    page: number;
    limit: number;
    total_pages: number;
  }> =>
    apiClient.get(`${API_PREFIX}/auth/invited-users?page=${page}&limit=${limit}`),

  // 用户备注管理
  // 添加或更新用户备注
  addUserNote: (userId: string, noteContent: string): Promise<UserNote> =>
    apiClient.post(`${API_PREFIX}/auth/users/${userId}/note`, { note_content: noteContent }),

  // 获取用户备注
  getUserNote: (userId: string): Promise<UserNote> =>
    apiClient.get(`${API_PREFIX}/auth/users/${userId}/note`),

  // 更新用户备注
  updateUserNote: (userId: string, noteContent: string): Promise<UserNote> =>
    apiClient.put(`${API_PREFIX}/auth/users/${userId}/note`, { note_content: noteContent }),

  // 删除用户备注
  deleteUserNote: (userId: string): Promise<{ message: string }> =>
    apiClient.delete(`${API_PREFIX}/auth/users/${userId}/note`),

  // 修改密码
  changePassword: (data: ChangePasswordRequest): Promise<{ message: string }> =>
    apiClient.post(`${API_PREFIX}/auth/change-password`, data),

  // API Key绑定管理
  // 绑定API Key
  bindApiKey: (data: BindApiKeyRequest): Promise<{ message: string; data: BoundApiKey }> =>
    apiClient.post(`${API_PREFIX}/auth/bind-api-key`, data),

  // 解绑API Key
  unbindApiKey: (data: UnbindApiKeyRequest): Promise<{ message: string }> =>
    apiClient.post(`${API_PREFIX}/auth/unbind-api-key`, data),

  // 获取绑定的API Key列表
  getBoundApiKeys: (): Promise<BoundApiKey[]> =>
    apiClient.get(`${API_PREFIX}/auth/bound-api-keys`),
};

// 邀请码相关API
export const inviteApi = {
  // 生成邀请码
  generate: (data: { max_uses?: number; expires_in_hours?: number }): Promise<{ code: string }> =>
    apiClient.post(`${API_PREFIX}/invite/generate`, data),

  // 公开领取邀请码（无需登录）
  generatePublic: (): Promise<{ code: string }> =>
    apiClient.post(`${API_PREFIX}/invite/public`),

  // 验证邀请码
  validate: (code: string): Promise<{ valid: boolean; message: string }> =>
    apiClient.post(`${API_PREFIX}/invite/validate`, { code }),

  // 获取邀请码列表
  list: (): Promise<{ codes: any[] }> =>
    apiClient.get(`${API_PREFIX}/invite/list`),

  // 停用邀请码
  deactivate: (id: string): Promise<{ message: string }> =>
    apiClient.patch(`${API_PREFIX}/invite/${id}/deactivate`),

  // 删除邀请码
  delete: (id: string): Promise<{ message: string }> =>
    apiClient.delete(`${API_PREFIX}/invite/${id}`),
};

// 健康检查API
export const healthApi = {
  check: (): Promise<{ status: string; message: string }> =>
    apiClient.get('/health'),
};

// 联系表单API（如果后端有的话）
export const contactApi = {
  // 提交联系表单
  submit: (data: ContactForm | ContactSubmissionRequest): Promise<{ 
    message: string;
    data?: {
      id: number;
      name: string;
      phone: string;
      type: string;
      plan?: string;
      status: string;
      priority: string;
      created_at: string;
    };
  }> =>
    apiClient.post(`${API_PREFIX}/contact/submit`, data),

  // 管理员API - 获取联系提交列表
  getContactSubmissions: (params?: ContactSubmissionListParams): Promise<ContactSubmissionListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.size) queryParams.set('size', params.size.toString());
    if (params?.status) queryParams.set('status', params.status);
    if (params?.type) queryParams.set('type', params.type);
    if (params?.priority) queryParams.set('priority', params.priority);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    return apiClient.get(`${API_PREFIX}/contact/list${queryString ? `?${queryString}` : ''}`);
  },

  // 管理员API - 获取联系提交详情
  getContactSubmissionDetail: (id: number): Promise<ContactSubmission> =>
    apiClient.get(`${API_PREFIX}/contact/${id}`),

  // 管理员API - 更新联系提交状态
  updateContactSubmission: (id: number, data: UpdateContactSubmissionRequest): Promise<{ message: string }> =>
    apiClient.put(`${API_PREFIX}/contact/${id}`, data),

  // 管理员API - 获取联系统计信息
  getContactStatistics: (): Promise<ContactStatistics> =>
    apiClient.get(`${API_PREFIX}/contact/statistics`),
  // 管理员API - 删除联系提交记录
  deleteContactSubmission: (id: number): Promise<{ message: string }> =>
    apiClient.delete(`${API_PREFIX}/contact/${id}`),
};

// 积分查询API - 支持多系统
export const statsApi = {
  // 根据API Key获取用户ID
  getKeyId: async (apiKey: string): Promise<{ success: boolean; data: { id: string } }> => {
    console.log('statsApi.getKeyId called with:', { apiKey });
    console.log('Making request to:', `${API_BASE_URL}${API_PREFIX}/stats/get-key-id`);
    
    // 积分查询API有特殊的响应格式，需要直接处理
    const url = `${API_BASE_URL}${API_PREFIX}/stats/get-key-id`;
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ apiKey })
    };

    console.log('Direct fetch request:', { url, config });

    try {
      const response = await fetch(url, config);
      console.log('Direct fetch response:', {
        status: response.status,
        statusText: response.statusText
      });
      
      const data = await response.json();
      console.log('Direct fetch data:', data);
      
      return data;
    } catch (error) {
      console.error('Direct fetch error:', error);
      throw new ApiError(500, '请求失败');
    }
  },

  // 根据用户ID获取积分统计
  getUserStats: async (apiId: string): Promise<{ success: boolean; data: any }> => {
    console.log('statsApi.getUserStats called with:', { apiId });
    console.log('Making request to:', `${API_BASE_URL}${API_PREFIX}/stats/user-stats`);
    
    // 积分查询API有特殊的响应格式，需要直接处理
    const url = `${API_BASE_URL}${API_PREFIX}/stats/user-stats`;
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ apiId })
    };

    console.log('Direct fetch request:', { url, config });

    try {
      const response = await fetch(url, config);
      console.log('Direct fetch response:', {
        status: response.status,
        statusText: response.statusText
      });
      
      const data = await response.json();
      console.log('Direct fetch data:', data);
      
      return data;
    } catch (error) {
      console.error('Direct fetch error:', error);
      throw new ApiError(500, '请求失败');
    }
  }
};

// 多系统开卡管理API
export const cardApi = {
  // 创建日卡 - 支持多系统
  createDayCard: (systemId?: string, permissions?: string): Promise<CreateCardResponse> => {
    const payload = {
      productModel: 'mini',
      concurrencyLimit: 3,
      dailyCostLimit: 12,
      ...(systemId && { cardSystemId: systemId }),
      ...(permissions && { permissions: permissions })
    };
    return apiClient.post(`${API_PREFIX}/card/multi/create-day-card`, payload);
  },

  // 创建周卡 - 支持多系统
  createWeekCard: (data: CreateCardRequest & { systemId?: string }): Promise<CreateCardResponse> => {
    const { systemId, ...cardData } = data;
    const payload = systemId ? { ...cardData, cardSystemId: systemId } : cardData;
    return apiClient.post(`${API_PREFIX}/card/multi/create-week-card`, payload);
  },

  // 创建月卡 - 支持多系统
  createMonthCard: (data: CreateCardRequest & { systemId?: string }): Promise<CreateCardResponse> => {
    const { systemId, ...cardData } = data;
    const payload = systemId ? { ...cardData, cardSystemId: systemId } : cardData;
    return apiClient.post(`${API_PREFIX}/card/multi/create-month-card`, payload);
  },

  // 查询API Key列表 - 支持多系统
  listApiKeys: async (params?: Omit<ApiKeyListParams, 'tags'> & { systemId?: string }): Promise<ApiKeyListResponse> => {
    // 获取当前用户信息
    let currentUser: User | null = null;
    try {
      currentUser = await authApi.me();
    } catch (error) {
      console.error('Failed to get current user:', error);
      // 如果获取用户信息失败，继续请求但不添加email tag
    }

    // 构建查询参数
    const queryParams = new URLSearchParams();
    
    // 基础参数
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.size) queryParams.set('size', params.size.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.mask !== undefined) queryParams.set('mask', params.mask.toString());
    if (params?.systemId) queryParams.set('system_id', params.systemId);

    // 添加当前用户邮箱作为tag过滤（如果获取到了用户信息）
    if (currentUser?.email) {
      queryParams.set('tags', currentUser.email);
      console.log('Adding user email as tag filter:', currentUser.email);
    }

    const queryString = queryParams.toString();
    const endpoint = `${API_PREFIX}/card/multi/list-api-keys${queryString ? `?${queryString}` : ''}`;
    
    console.log('Requesting API keys list:', endpoint);
    return apiClient.get<ApiKeyListResponse>(endpoint);
  },

  // 删除API Key - 支持多系统
  deleteApiKey: (keyId: string, systemId?: string): Promise<{ message: string }> => {
    const queryParams = new URLSearchParams();
    if (systemId) queryParams.set('system_id', systemId);
    const queryString = queryParams.toString();
    return apiClient.delete(`${API_PREFIX}/card/multi/delete-api-key/${keyId}${queryString ? `?${queryString}` : ''}`);
  },
};

// 系统管理API
export const systemApi = {
  // 获取系统选择列表
  getSystemsForSelection: (): Promise<Array<{ id: string; name: string; interface_url: string; claude_api_url: string; openai_api_url: string; description?: string }>> =>
    apiClient.get(`${API_PREFIX}/card-system/selection`),
};

// FAQ相关API
export const faqApi = {
  // 公开API - 获取FAQ列表
  getPublicFAQList: (category?: string): Promise<FAQPublicResponse[]> => {
    const queryParams = new URLSearchParams();
    if (category) queryParams.set('category', category);
    const queryString = queryParams.toString();
    return apiClient.get(`${API_PREFIX}/faq/list${queryString ? `?${queryString}` : ''}`);
  },

  // 公开API - 获取FAQ分类列表
  getFAQCategories: async (): Promise<string[]> => {
    const result = await apiClient.get<string[]>(`${API_PREFIX}/faq/categories`);
    return result || [];
  },

  // 公开API - 增加FAQ查看次数
  incrementFAQViewCount: (id: string): Promise<string> =>
    apiClient.post(`${API_PREFIX}/faq/${id}/view`),

  // 管理员API - 获取FAQ列表（支持分页、搜索）
  getAdminFAQList: async (params?: FAQListParams): Promise<FAQListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.size) queryParams.set('size', params.size.toString());
    if (params?.category) queryParams.set('category', params.category);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.is_active !== undefined) queryParams.set('is_active', params.is_active.toString());

    const queryString = queryParams.toString();
    const result = await apiClient.get<FAQListResponse>(`${API_PREFIX}/faq/admin/list${queryString ? `?${queryString}` : ''}`);
    return {
      total: result?.total || 0,
      items: result?.items || []
    };
  },

  // 管理员API - 创建FAQ
  createFAQ: (data: FAQCreateRequest): Promise<FAQ> =>
    apiClient.post(`${API_PREFIX}/faq/admin/create`, data),

  // 管理员API - 根据ID获取FAQ
  getFAQById: (id: string): Promise<FAQ> =>
    apiClient.get(`${API_PREFIX}/faq/admin/${id}`),

  // 管理员API - 更新FAQ
  updateFAQ: (id: string, data: FAQUpdateRequest): Promise<FAQ> =>
    apiClient.put(`${API_PREFIX}/faq/admin/${id}`, data),

  // 管理员API - 删除FAQ
  deleteFAQ: (id: string): Promise<string> =>
    apiClient.delete(`${API_PREFIX}/faq/admin/${id}`),

  // 管理员API - 更新FAQ排序
  updateFAQSortOrder: (id: string, data: FAQSortRequest): Promise<string> =>
    apiClient.put(`${API_PREFIX}/faq/admin/${id}/sort`, data),
};

// 认证工具函数
export const authUtils = {
  // 保存认证token
  saveToken: (token: string) => {
    localStorage.setItem('auth_token', token);
  },

  // 获取认证token
  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },

  // 清除认证token
  clearToken: () => {
    localStorage.removeItem('auth_token');
  },

  // 检查是否已登录
  isLoggedIn: (): boolean => {
    return !!localStorage.getItem('auth_token');
  },
};

// 学习进度API
import { UserLearningProgressResponse, UpdateLearningProgressRequest, UserProgress } from '@/types/learning';

export const learningApi = {
  // 获取用户学习进度
  getProgress: async (): Promise<UserProgress> => {
    const response = await apiClient.get<UserLearningProgressResponse>(`${API_PREFIX}/learning/progress`);
    // 转换后端格式到前端格式（字段名从snake_case转为camelCase）
    return {
      userId: response.user_id,
      totalXP: response.total_xp,
      level: response.level,
      completedTasks: response.completed_tasks || [],
      currentTask: response.current_task,
      achievements: response.achievements || [],
      lastActivityAt: response.last_activity_at
    };
  },

  // 更新用户学习进度
  updateProgress: async (data: UpdateLearningProgressRequest): Promise<UserProgress> => {
    // 转换前端格式到后端格式（字段名从camelCase转为snake_case）
    // 注意：completed_tasks 和 achievements 直接作为数组发送
    const payload = {
      total_xp: data.total_xp,
      level: data.level,
      completed_tasks: data.completed_tasks,  // 直接发送数组
      current_task: data.current_task,
      achievements: data.achievements  // 直接发送数组
    };
    const response = await apiClient.post<UserLearningProgressResponse>(`${API_PREFIX}/learning/progress`, payload);
    // 转换后端格式到前端格式（字段名从snake_case转为camelCase）
    return {
      userId: response.user_id,
      totalXP: response.total_xp,
      level: response.level,
      completedTasks: response.completed_tasks || [],
      currentTask: response.current_task,
      achievements: response.achievements || [],
      lastActivityAt: response.last_activity_at
    };
  }
};

// AI应用管理API
export const aiAppApi = {
  // 创建AI应用
  createApp: async (data: CreateAppRequest): Promise<AIApp> => {
    return await apiClient.post<AIApp>(`${API_PREFIX}/apps`, data);
  },

  // 根据ID获取应用详情
  getAppById: async (id: string): Promise<AppDetailResponse> => {
    return await apiClient.get<AppDetailResponse>(`${API_PREFIX}/apps/${id}`);
  },

  // 根据Slug获取应用详情
  getAppBySlug: async (slug: string): Promise<AppDetailResponse> => {
    return await apiClient.get<AppDetailResponse>(`${API_PREFIX}/apps/slug/${slug}`);
  },

  // 更新AI应用
  updateApp: async (id: string, data: UpdateAppRequest): Promise<AIApp> => {
    return await apiClient.put<AIApp>(`${API_PREFIX}/apps/${id}`, data);
  },

  // 删除AI应用
  deleteApp: async (id: string): Promise<DeleteResponse> => {
    return await apiClient.delete<DeleteResponse>(`${API_PREFIX}/apps/${id}`);
  },

  // 获取应用列表
  getAppList: async (params?: AppListRequest): Promise<AppListResponse> => {
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.size) queryParams.append('size', params.size.toString());
      if (params.app_type) queryParams.append('app_type', params.app_type);
      if (params.status) queryParams.append('status', params.status);
      if (params.category) queryParams.append('category', params.category);
      if (params.keyword) queryParams.append('keyword', params.keyword);
      if (params.is_featured !== undefined) queryParams.append('is_featured', params.is_featured.toString());
      if (params.is_hot !== undefined) queryParams.append('is_hot', params.is_hot.toString());
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${API_PREFIX}/apps?${queryString}` : `${API_PREFIX}/apps`;

    return await apiClient.get<AppListResponse>(url);
  },

  // 下载应用
  downloadApp: async (id: string): Promise<DownloadResponse> => {
    return await apiClient.post<DownloadResponse>(`${API_PREFIX}/apps/${id}/download`);
  },

  // 获取应用分类列表
  getCategories: async (): Promise<string[]> => {
    return await apiClient.get<string[]>(`${API_PREFIX}/public/apps/categories`);
  },

  // 获取推荐应用
  getFeaturedApps: async (limit?: number): Promise<AIApp[]> => {
    const queryParams = limit ? `?limit=${limit}` : '';
    return await apiClient.get<AIApp[]>(`${API_PREFIX}/public/apps/featured${queryParams}`);
  },

  // 获取热门应用
  getHotApps: async (limit?: number): Promise<AIApp[]> => {
    const queryParams = limit ? `?limit=${limit}` : '';
    return await apiClient.get<AIApp[]>(`${API_PREFIX}/public/apps/hot${queryParams}`);
  },

  // 根据分类获取应用
  getAppsByCategory: async (category: string, limit?: number): Promise<AIApp[]> => {
    const queryParams = limit ? `?limit=${limit}` : '';
    return await apiClient.get<AIApp[]>(`${API_PREFIX}/public/apps/category/${category}${queryParams}`);
  },

  // 获取应用统计信息（管理员）
  getStats: async (): Promise<AppStats> => {
    return await apiClient.get<AppStats>(`${API_PREFIX}/apps/admin/stats`);
  },

  // 批量更新应用状态（管理员）
  batchUpdateStatus: async (data: BatchUpdateStatusRequest): Promise<BatchUpdateStatusResponse> => {
    return await apiClient.put<BatchUpdateStatusResponse>(`${API_PREFIX}/apps/admin/batch-status`, data);
  }
};

// 文件上传API
export const uploadApi = {
  // 上传文件
  uploadFile: async (file: File): Promise<{
    file_name: string;
    original_name: string;
    file_size: number;
    file_path: string;
    url: string;
    message: string;
  }> => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}${API_PREFIX}/upload`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: formData
    });

    const result = await response.json();

    if (result.code !== 200) {
      throw new ApiError(result.code, result.message || '上传失败');
    }

    return result.data;
  }
};

export { apiClient, ApiClient };

// 导出AI聊天API
export * from './aiChatApi';